/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-filename-extension */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment model used to render the dashboards
 *
 * @file Assignment.js
 * @author Satyaki Bose
 */

import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import { AET_STORE_EVENTS } from '../common/Constants';

const CourseSchedule = types.model({
  dayValue: types.maybeNull(types.number),
  startTime: types.maybeNull(types.string),
  endTime: types.maybeNull(types.string),
  active: types.maybeNull(types.boolean),
  overlap: types.maybeNull(types.boolean)
});

const Instructor = types.model({
  id: types.maybeNull(types.string),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  profileImage: types.maybeNull(types.string)
});

const AssignmentModel = types
  .model({
    courseId: types.maybeNull(types.string),
    courseTitle: types.maybeNull(types.string),
    courseSchedule: types.optional(types.array(CourseSchedule), []),
    instructors: types.optional(types.array(Instructor), [])
  })
  .actions(self => (
    {
      fetch(courseId) {
        Framework.getEventManager().publish(AET_STORE_EVENTS.INSTRUCTOR_REQUESTED, { courseId });
      },
      set(data) {
        applySnapshot(self, data);
      }
    }
  )).views(self => ({
    getInstructorName() {
      if (self.instructors.length === 0) {
        return '';
      }
      return `${self.instructors[0].firstName} ${self.instructors[0].lastName}`;
    },
    getInstructorInitials() {
      if (self.instructors.length === 0) {
        return '';
      }
      return `${self.instructors[0].firstName.charAt(0)}${self.instructors[0].lastName.charAt(0)}`.toUpperCase();
    },
    getClassSchedule() {
      const days = [
        { label: 'Sun', value: 0 }, { label: 'Mon', value: 1 },
        { label: 'Tue', value: 2 }, { label: 'Wed', value: 3 },
        { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 },
        { label: 'Sat', value: 6 }];
      const sortedSchedule = [...self.courseSchedule]
        .sort((a, b) => {
          if (a.dayValue !== b.dayValue) {
            return a.dayValue - b.dayValue;
          }
          // If dayValue is equal, sort by startTime
          return new Date(a.startTime) - new Date(b.startTime);
        });

      const schedule = sortedSchedule.map(({ dayValue, startTime }) => {
        const day = days.find(({ value }) => value === dayValue);
        const label = day ? day.label : '';
        return {
          label,
          time: new Date(startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }).replace(
            /\s(AM|PM)/i, match => match.trim()
          )
        };
      });

      const scheduleSameDaytimeMerge = schedule.reduce((acc, curr) => {
        const existingItem = acc.find(item => item.label === curr.label);
        if (existingItem) {
          existingItem.time = `${existingItem.time}, ${curr.time}`;
        } else {
          acc.push(curr);
        }
        return acc;
      }, []);

      return scheduleSameDaytimeMerge;
    }
  }));

export default AssignmentModel;
